import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface SEOProps {
  title: string;
  description: string;
  structuredData?: object[];
  noindex?: boolean;
  canonicalUrl?: string;
  breadcrumbs?: BreadcrumbItem[];
  keywords?: string;
  image?: string;
  type?: string;
  section?: string;
  tags?: string[];
  [key: string]: any;
}

const ComprehensiveSEO: React.FC<SEOProps> = ({
  title,
  description,
  structuredData = [],
  noindex = false,
  canonicalUrl,
  breadcrumbs = []
}) => {
  const location = useLocation();
  const currentUrl = `https://tinyminds.in${location.pathname}`;
  const finalCanonicalUrl = canonicalUrl || currentUrl;

  const fullTitle = title.includes("TinyMinds")
    ? title
    : `${title} | TinyMinds`;

  /* Breadcrumb Schema (optional & safe) */
  const breadcrumbSchema =
    breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.label,
            "item": item.href
              ? `https://tinyminds.in${item.href}`
              : undefined
          })).filter(item => item.item)
        }
      : null;

  const finalSchema = [
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
    ...structuredData
  ];

  return (
    <Helmet>
      {/* Primary SEO */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Indexing control */}
      <meta
        name="robots"
        content={noindex ? "noindex,nofollow" : "index,follow"}
      />
      <meta
        name="googlebot"
        content={noindex ? "noindex,nofollow" : "index,follow"}
      />

      {/* Canonical */}
      <link rel="canonical" href={finalCanonicalUrl} />

      {/* Language */}
      <link rel="alternate" hrefLang="en-IN" href={finalCanonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={finalCanonicalUrl} />

      {/* Per-route Open Graph (overrides static index.html) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={finalCanonicalUrl} />

      {/* Per-route Twitter */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* Structured Data (FACTUAL ONLY) */}
      {finalSchema.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
};

export default ComprehensiveSEO;
